import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/layout/app-shell";
import { ErrorBoundary } from "./components/system/error-boundary";
import { RouteFallback } from "./components/system/route-fallback";
import { useAppStore } from "./state/app-store";
import { ViewportManager } from "./components/system/viewport-manager";

const AuthPage = lazy(() => import("./pages/auth-page"));
const HomePage = lazy(() => import("./pages/home-page"));
const CustomersPage = lazy(() => import("./pages/customers-page"));
const CustomerWorkspacePage = lazy(() => import("./pages/customer-workspace-page"));
const ResourcesPage = lazy(() => import("./pages/resources-page"));
const ProfilePage = lazy(() => import("./pages/profile-page"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: 1,
      refetchOnWindowFocus: false
    },
    mutations: { retry: 0 }
  }
});

function ProtectedShell() {
  const mode = useAppStore((state) => state.mode);
  const clearMode = useAppStore((state) => state.clearMode);
  const cloudSession = useQuery({
    queryKey: ["cloud-session"],
    queryFn: async () => (await import("./data/supabase/auth")).authService.getSession(),
    enabled: mode === "cloud",
    retry: false,
    staleTime: 60_000
  });

  useEffect(() => {
    if (mode === "cloud" && !cloudSession.isLoading && (!cloudSession.data || cloudSession.error)) {
      clearMode();
    }
  }, [clearMode, cloudSession.data, cloudSession.error, cloudSession.isLoading, mode]);

  if (!mode) return <Navigate to="/auth" replace />;
  if (mode === "cloud" && cloudSession.isLoading) return <RouteFallback />;
  if (mode === "cloud" && (!cloudSession.data || cloudSession.error)) return <Navigate to="/auth" replace />;
  return <AppShell />;
}

export function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <ViewportManager />
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/auth/*" element={<AuthPage />} />
              <Route element={<ProtectedShell />}>
                <Route index element={<HomePage />} />
                <Route path="/customers" element={<CustomersPage />} />
                <Route path="/customers/:customerId" element={<CustomerWorkspacePage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/me" element={<ProfilePage />} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
